import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface AboutHeroSectionProps {
  language?: 'ar' | 'en';
  titleAr?: string;
  titleEn?: string;
  subtitleAr?: string;
  subtitleEn?: string;
  backgroundImage?: string;
}

const AboutHeroSection: React.FC<AboutHeroSectionProps> = ({
  language = 'ar',
  titleAr = "مكة البركة للمقاولات العامة",
  titleEn = "Mecca El Baraka For Construction",
  subtitleAr = "نبني المستقبل بالجودة والخبرة والحلول المتكاملة في المقاولات والمحاجر بمصر والشرق الأوسط",
  subtitleEn = "Building the Future with Quality, Expertise and Integrated Solutions in Construction and Quarries in Egypt and the Middle East",
  backgroundImage = "/images/about-hero.jpg"
}) => {
  const isRTL = language === 'ar';
  const [showHeroText, setShowHeroText] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  const heroTitle = isRTL ? titleAr : titleEn;
  const heroSubtitle = isRTL ? subtitleAr : subtitleEn;

  useEffect(() => {
    let fadeOutTimeout: NodeJS.Timeout;
    let hideTimeout: NodeJS.Timeout;
    if (showHeroText) {
      fadeOutTimeout = setTimeout(() => setFadeOut(true), 5000);
      hideTimeout = setTimeout(() => setShowHeroText(false), 6200);
    }
    return () => {
      clearTimeout(fadeOutTimeout);
      clearTimeout(hideTimeout);
    };
  }, [showHeroText]);

  // Split text to words (preserve spaces)
  const splitWords = (text: string) => text.split(/(\s+)/);

  return (
    <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 py-16 md:py-20 flex items-center justify-center min-h-[200px] md:min-h-[300px]">
      <img src={backgroundImage} alt="Hero Background" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="container-custom relative z-10 text-center text-white">
        <AnimatePresence>
          {showHeroText && (
            <motion.div key="hero-text" className="inline-block">
              <div className="overflow-hidden">
                <motion.h1
                  className="text-4xl md:text-6xl font-bold mb-4 whitespace-pre-wrap"
                  style={{ display: 'inline-block' }}
                >
                  {splitWords(heroTitle).map((word, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, x: -40 }}
                      animate={fadeOut ? { opacity: 0, x: 40, transition: { duration: 0.7, delay: idx * 0.12 } } : { opacity: 1, x: 0, transition: { duration: 0.7, delay: idx * 0.12 } }}
                      exit={{ opacity: 0, x: 40, transition: { duration: 0.7, delay: idx * 0.12 } }}
                      style={{ display: 'inline-block', whiteSpace: word.trim() === '' ? 'pre' : 'normal' }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.h1>
              </div>
              <div className="overflow-hidden">
                <motion.p
                  className="text-xl md:text-2xl font-semibold mb-2 whitespace-pre-wrap max-w-4xl mx-auto"
                  style={{ display: 'inline-block' }}
                >
                  {splitWords(heroSubtitle).map((word, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, x: -40 }}
                      animate={fadeOut ? { opacity: 0, x: 40, transition: { duration: 0.7, delay: idx * 0.09 } } : { opacity: 1, x: 0, transition: { duration: 0.7, delay: idx * 0.09 } }}
                      exit={{ opacity: 0, x: 40, transition: { duration: 0.7, delay: idx * 0.09 } }}
                      style={{ display: 'inline-block', whiteSpace: word.trim() === '' ? 'pre' : 'normal' }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default AboutHeroSection; 